<script setup lang="ts">
import { computed, onMounted, reactive, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { captureException } from "@sentry/vue";
import { PensionWiseBooking } from "../stores/mainView";
import { Question, Radio } from "../components/fields";
import { insertPensionWiseAppointmentChoice } from "../data/apply";
import { useToast } from "../services/toast";
import config from "../config";
import { useQuoteStore } from "../stores/quotes";
import { useConfigStore } from "@/stores/config";
import { ApplyPageStages, useApplicationStore } from "../stores/application";
import { ensureDefined } from "../utils/types";
import { LINKS } from "@/constants/links";
import { getCompanyIdBySlug, parseQueryQuoteNumber } from "@/utils";
import axios from "axios";
import { sortedPremiums } from "@/utils/quote";
import Modal from "../components/Modal.vue";
import QuoteSummary from "../components/QuoteSummary.vue";

type PensionwiseNudge = {
    appointmentChoice: PensionWiseBooking | null,
    accOpen: boolean,
    previousSelectedPensionWiseBooking: PensionWiseBooking | null,
    quotePackGenerated: boolean,
    showQuoteSummaryModal: boolean,
    activeAccordion: "pensionwise_info" | "pensionwise_book" | null,
}

const route = useRoute();
const router = useRouter();
const quoteStore = useQuoteStore();
const applicationStore = useApplicationStore();
const configStore = useConfigStore();
const { toast } = useToast();

const PreviousSelectionTermMap = {
    [PensionWiseBooking.BookMyOwn]: "I will book my own appointment",
    [PensionWiseBooking.BookForMe]: "I would like you to book me a Pension Wise appointment",
    [PensionWiseBooking.AlreadyHadAdvice]: "I have had financial advice",
    [PensionWiseBooking.AlreadyHadOne]: "I have received Pension Wise guidance",
    [PensionWiseBooking.AlreadyHaveAppointment]: "I have already booked an appointment but haven’t had it yet",
    [PensionWiseBooking.ProceedWithout]: "I am happy to proceed without guidance or advice",
};

const state: PensionwiseNudge = reactive({
    appointmentChoice: null,
    accOpen: false,
    previousSelectedPensionWiseBooking: null,
    quotePackGenerated: false,
    showQuoteSummaryModal: false,
    activeAccordion: null,
});

const isExtranetHandOff = configStore.extranetHandoff;
const quoteNumber = computed(() => parseQueryQuoteNumber(route.query?.quoteNumber) ?? "");
const premiumIdParam = computed(() => parseQueryQuoteNumber(route.query?.premiumId));
const pwBookOpen = computed(() => state.activeAccordion === "pensionwise_book");
const pwInfoOpen = computed(() => state.activeAccordion === "pensionwise_info");
const quote = computed(() => ensureDefined(quoteStore.quote));
const showExtranetHandoffContent = computed(() => isExtranetHandOff && state.quotePackGenerated);
const progressBlocked =
    computed(() => state.appointmentChoice === null || choiceRequiresAction(state.appointmentChoice));

const title = computed(() => {
    if (showExtranetHandoffContent.value) {
        return `Quote pack has been generated for: ${quoteNumber.value}`;
    } else {
        return choiceRequiresAction(state.previousSelectedPensionWiseBooking)
            ? "A reminder about Pension Wise"
            : "Would you like free guidance from Pension Wise?";
    }
});

watch(() => state.appointmentChoice, async choice => {
    if (choice !== null) {
        try {
            await insertBookingChoice();
        } catch (err) {
            captureException(err);
            toast({
                timeout: config.toastTimeout,
                title: "Error",
                message: "Please try again in a few minutes.",
            });
        }
    }
});

onMounted(async () => {
    if (
        !applicationStore.quoteNumber ||
        applicationStore.quoteNumber !== quoteNumber.value ||
        applicationStore.premiumId !== Number(premiumIdParam.value)
    ) {
        const premium = await quoteStore.getPremium(quoteNumber.value || undefined, premiumIdParam.value);
        applicationStore.setApplicationDetails(premium, quoteNumber.value, ensureDefined(quoteStore.quote));
    }
    state.previousSelectedPensionWiseBooking = quoteStore.quote?.appointmentChoice ?? null;
});

const choiceRequiresAction = (choice: PensionWiseBooking | null): choice is PensionWiseBooking => {
    if (choice === null) {
        return false;
    }

    const bookingActionRequiredChoices = [
        PensionWiseBooking.BookForMe,
        PensionWiseBooking.BookMyOwn,
        PensionWiseBooking.AlreadyHaveAppointment,
    ];

    return bookingActionRequiredChoices.includes(choice);
};

const toggleAccordion = (accordion: "pensionwise_info" | "pensionwise_book") => {
    if (state.activeAccordion === accordion) {
        state.activeAccordion = null;
    } else {
        state.activeAccordion = accordion;
    }
};

const insertBookingChoice = async () => {
    await insertPensionWiseAppointmentChoice(
        state.appointmentChoice ?? "" as PensionWiseBooking,
        quoteNumber.value,
        quoteStore.latestTokenByQuoteNumber(quoteNumber.value).token);
};

const goToResults = () => {
    router.push({ name: "results", query: { quotenumber: quoteNumber.value } });
};

const handleApplyClick = async () => {
    if (!applicationStore.quoteNumber) {
        return;
    }
    try {
        await insertBookingChoice();
    } catch (err) {
        captureException(err);
        toast({
            timeout: config.toastTimeout,
            title: "Error",
            message: "Please try again in a few minutes.",
        });
        return;
    }
    router.push({
        name: ApplyPageStages[ApplyPageStages.Information],
        query: { premiumId: applicationStore.premiumId, quoteNumber: quoteNumber.value },
    });
};

const generateTopThreePacks = async () => {
    state.quotePackGenerated = true;
    const currentQuote = quoteStore.quote;
    const applicationData = {
        customer: {
            forename: currentQuote?.customer?.forename,
            surname: currentQuote?.customer?.surname,
            phone: currentQuote?.customer?.phone ?? undefined,
            dob: currentQuote?.customer?.dob,
            email: currentQuote?.customer?.email,
            address: currentQuote?.customer?.address,
        },
        product: {},
        ...(currentQuote?.dependant && {
            dependant: {
                forename: currentQuote?.dependant?.forename,
                surname: currentQuote?.dependant?.surname,
                dob: currentQuote?.dependant?.dob,
                address: currentQuote?.dependant?.address,
            },
        }),
    };
    try {
        await insertBookingChoice();
        const applicationPromises = sortedPremiums(quoteStore.quotations, 3).map(async premium => {
            return axios.post(
                `${configStore.apiUrl}/v1/annuities/quote/${quoteNumber.value}/premium/${premium.premiumId}/create-application`,
                {
                    ...applicationData,
                },
                {
                    headers: {
                        Authentication: quoteStore.latestTokenByQuoteNumber(quoteNumber.value).token,
                    },
                    validateStatus: () => true,
                },
            );
        });
        await Promise.all(applicationPromises);
    } catch (err) {
        captureException(err);
        toast({
            timeout: config.toastTimeout,
            title: "Could not create application documents",
            message: "",
        });
    }
};

</script>
<template>
    <div>
        <div class="row">
            <div class="col-11">
                <h5
                    id="modal-title"
                    class="modal-title ml-auto mr-auto blue mb-3"
                >
                    {{ title }}
                </h5>
            </div>
        </div>
    </div>
    <div v-if="!showExtranetHandoffContent">
        <div v-if="choiceRequiresAction(state.previousSelectedPensionWiseBooking)">
            <p class="previous-answer-heading mb-2">
                <strong>You previously told us:</strong>
            </p>
            <p
                v-if="state.previousSelectedPensionWiseBooking"
                class="previous-answer-text mb-2"
            >
                {{ PreviousSelectionTermMap[state.previousSelectedPensionWiseBooking] }}
            </p>
        </div>
        <div>
            <button
                id="pwbHeader"
                type="button"
                class="pt-3 pb-3 ps-3 accordion-header"
                :class="pwInfoOpen ? '' : 'mb-3'"
                :aria-expanded="pwInfoOpen"
                aria-controls="pwInfoAccBody"
                @click.prevent="toggleAccordion('pensionwise_info')"
            >
                <span>
                    What is Pension Wise?
                    <font-awesome-icon :icon="pwInfoOpen ? ['fas', 'chevron-up'] : ['fas', 'chevron-down']" />
                </span>
            </button>
            <div
                v-if="pwInfoOpen"
                id="pwInfoAccBody"
                class="accordion-body small-text mb-3"
            >
                <p class="mb-3">
                    Pension Wise is the government's free, impartial service, offering retirement guidance to help you understand how and when you can take money from your defined contribution pension pot.
                </p>
                <p class="mb-3">
                    The guidance is provided through a telephone appointment with an independent pension specialist, where you can talk through the different options available to you and make an informed decision on what to do. Pension Wise won't give you a personal recommendation.
                </p>
                <p class="mb-3">
                    Alternatively, you could speak to a financial adviser to get regulated advice and a personal recommendation. This usually comes at your own cost.
                </p>
            </div>
            <button
                id="pwbBookHeader"
                type="button"
                class="pt-3 pb-3 ps-3 accordion-header"
                :class="pwBookOpen ? '' : 'mb-3'"
                :aria-expanded="pwBookOpen"
                aria-controls="pwbBookAccBody"
                @click.prevent="toggleAccordion('pensionwise_book')"
            >
                <span>
                    How do I book an appointment?
                    <font-awesome-icon :icon="pwBookOpen ? ['fas', 'chevron-up'] : ['fas', 'chevron-down']" />
                </span>
            </button>
            <div
                v-if="pwBookOpen"
                id="pwbBookAccBody"
                class="accordion-body small-text mb-3"
            >
                <p class="mb-3">
                    We can book a free Pension Wise appointment for you or you can book an appointment by calling 0800 138 3944 or using their <a
                        href="https://www.moneyhelper.org.uk/en/pensions-and-retirement/pension-wise/book-a-free-pension-wise-appointment"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        online booking form</a>.
                </p>
                <p class="mb-3">
                    If you decide to speak to Pension Wise, <b>simply return to Annuity Ready afterwards to complete an application with your chosen provider.</b>
                </p>
            </div>
        </div>
    </div>
    <div v-if="!showExtranetHandoffContent">
        <div class="row">
            <Question
                type="group"
                label="Would you like free guidance from Pension Wise?"
                label-class="visually-hidden"
                input-class="col-12"
                :include-padding="false"
                name="pensionWiseAppointment"
            >
                <div class="col-12">
                    <div class="pwNudge">
                        <Radio
                            v-model="state.appointmentChoice"
                            radio-type="standard"
                            child-class="text-start"
                            title="I would like Annuity Ready to book a Pension Wise appointment for me"
                            :value="PensionWiseBooking.BookForMe"
                            :validation="{}"
                        />
                        <div
                            v-if="state.appointmentChoice === PensionWiseBooking.BookForMe"
                            id="general-alert-bfm"
                            class="alert alert-warning hideAfter mt-2 modal-alert"
                        >
                            <p
                                class="mb-3"
                            >
                                Please call us on <a href="tel:08003265479">0800 326 5479</a> between 9am to 5pm, Monday to Friday, so we can book your appointment with Pension Wise. We may record and monitor calls.
                            </p>
                            <p class="mb-3">
                                Following your Pension Wise appointment, simply return to Annuity Ready afterwards to complete an application with your chosen provider.
                            </p>
                            <b>You can return to Annuity Ready from your quote email or www.annuityready.com</b>
                        </div>
                        <Radio
                            v-model="state.appointmentChoice"
                            radio-type="standard"
                            child-class="text-start"
                            title="I will book my own Pension Wise appointment"
                            :value="PensionWiseBooking.BookMyOwn"
                            :validation="{}"
                        />
                        <div
                            v-if="state.appointmentChoice === PensionWiseBooking.BookMyOwn"
                            id="general-alert-bmo"
                            class="alert alert-warning hideAfter mt-2 modal-alert"
                        >
                            <p
                                class="mb-3"
                            >
                                Please call Pension Wise on <a href="tel:08001383944">0800 138 3944</a> or use their
                                <a
                                    href="https://www.moneyhelper.org.uk/en/pensions-and-retirement/pension-wise/book-a-free-pension-wise-appointment"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >online booking form</a>
                            </p>
                            <p class="mb-3">
                                Following your Pension Wise appointment, simply return to Annuity Ready afterwards to complete an application with your chosen provider.
                            </p>
                            <b>You can return to Annuity Ready from your quote email or www.annuityready.com</b>
                        </div>
                        <Radio
                            v-model="state.appointmentChoice"
                            radio-type="standard"
                            child-class="text-start"
                            title="I have already booked an appointment but haven't had it yet"
                            :value="PensionWiseBooking.AlreadyHaveAppointment"
                            :validation="{}"
                        />
                        <div
                            v-if="state.appointmentChoice === PensionWiseBooking.AlreadyHaveAppointment"
                            id="notHadYet-alert"
                            class="alert alert-warning hideAfter mt-2 modal-alert"
                        >
                            <p class="mb-3">
                                You cannot proceed at this time. Once you've had your appointment, you can return to complete an application with your chosen provider.
                            </p>
                            <p class="mb-3">
                                Following your Pension Wise appointment, simply return to Annuity Ready afterwards to complete an application with your chosen provider.
                            </p>
                            <b class="mb-3">You can return to Annuity Ready from your quote email or www.annuityready.com</b>
                        </div>
                        <Radio
                            v-model="state.appointmentChoice"
                            radio-type="standard"
                            child-class="text-start"
                            title="I have already received Pension Wise guidance and would like to proceed"
                            :value="PensionWiseBooking.AlreadyHadOne"
                            :validation="{}"
                        />
                        <div
                            v-if="state.appointmentChoice === PensionWiseBooking.AlreadyHadOne"
                            id="booking-alreadyHad-alert"
                            class="alert alert-warning hideAfter mt-2 modal-alert"
                        >
                            <p>If your circumstances have changed or the value of your pension has changed significantly since you received guidance you may benefit from receiving the guidance again.</p>
                        </div>
                        <Radio
                            v-model="state.appointmentChoice"
                            radio-type="standard"
                            child-class="text-start"
                            title="I have already received financial advice and would like to proceed"
                            :value="PensionWiseBooking.AlreadyHadAdvice"
                            :validation="{}"
                        />
                        <div
                            v-if="state.appointmentChoice === PensionWiseBooking.AlreadyHadAdvice"
                            id="booking-advised-alert"
                            class="alert alert-warning hideAfter mt-2 modal-alert"
                        >
                            <p>
                                If your circumstances have changed or the value of your pension has changed significantly since you received advice you may benefit from receiving the advice again or speaking to Pension Wise for guidance.
                            </p>
                        </div>
                        <Radio
                            v-model="state.appointmentChoice"
                            radio-type="standard"
                            child-class="text-start"
                            title="I would like to proceed without guidance or advice"
                            :value="PensionWiseBooking.ProceedWithout"
                            :validation="{}"
                        />
                        <div
                            v-if="state.appointmentChoice === PensionWiseBooking.ProceedWithout"
                            id="booking-danger-without-alert"
                            class="alert alert-warning hideAfter mt-2 modal-alert"
                        >
                            <p>
                                The decisions you make about your pension pot are important and you may not be able to change your mind later on. The guidance from Pension Wise could help you make a more informed decision.
                            </p>
                        </div>
                    </div>
                </div>
            </Question>
        </div>
    </div>
    <div v-else>
        <div class="col-lg-12 p-0 float-left m-2 bg-pdf mx-auto">
            <h6
                id="extranet-alert"
                class="alert alert-warning-box alert-dismissible fade show mb-4"
            >
                These documents need printing from here
            </h6>
            <h6 class="text-center blue chalet pt-3 font-weight-bold">
                Annuity Ready specific documents
            </h6>
            <div class="col-lg-12 float-left m-2 bg-pdf mx-auto package-download">
                <div class="row">
                    <div class="col-md-6 col-sm-12 p-2 text-left float-left">
                        <a
                            id="terms-conditions-anchor"
                            target="_blank"
                            :href="LINKS.ANNUITY_TERMS_AND_CONDITIONS"
                        >
                            <img
                                src="@/assets/images/terms-conditions.svg"
                                class="d-inline-block extranet-icon"
                                alt="Terms and conditions"
                            >
                            <p class="d-inline-block text-info">
                                Terms &amp; Conditions PDF
                            </p>
                        </a>
                    </div>
                    <div class="col-md-6 col-sm-12 p-2 text-left float-left">
                        <a
                            id="demands-and-needs-anchor"
                            target="_blank"
                            :href="`/demands-and-needs/${quoteNumber}`"
                        >
                            <img
                                src="@/assets/images/demands.svg"
                                class="d-inline-block extranet-icon"
                                alt="Demands and needs"
                            >
                            <p class="d-inline-block text-info">
                                Demands and needs
                            </p>
                        </a>
                    </div>
                    <div class="col-md-6 col-sm-12 p-2 text-left float-left">
                        <a
                            id="our-services-anchor"
                            target="_blank"
                            href="/our-services"
                        >
                            <img
                                src="@/assets/images/services.svg"
                                class="d-inline-block extranet-icon"
                                alt="Our Services"
                            >

                            <p class="d-inline-block text-info">
                                Our Services
                            </p>
                        </a>
                    </div>
                    <div class="col-md-6 col-sm-12 p-2 text-left float-left">
                        <a
                            id="quote-results-anchor"
                            target="_blank"
                            :href="`/results-pretty?quotenumber=${quoteNumber}`"
                        >
                            <img
                                src="@/assets/images/results.svg"
                                class="d-inline-block extranet-icon"
                                alt="Quote results"
                            >
                            <p class="d-inline-block text-info">
                                Quote results
                            </p>
                        </a>
                    </div>
                    <div class="col-md-6 col-sm-12 p-2 text-left float-left">
                        <a
                            id="second-line-anchor"
                            target="_blank"
                            href="/static/pdf/2nd_line_defence.pdf"
                        >
                            <img
                                src="@/assets/images/questions.svg"
                                class="d-inline-block extranet-icon"
                                alt="Second line of defence statements"
                            >
                            <p class="d-inline-block text-info">
                                Second line of defence statements
                            </p>
                        </a>
                    </div>
                    <div class="col-md-6 col-sm-12 p-2 text-left float-left">
                        <a
                            id="quote-summary-anchor"
                            target="_blank"
                            href="#"
                            @click.prevent="state.showQuoteSummaryModal = true"
                        >
                            <img
                                src="@/assets/images/summary.svg"
                                class="d-inline-block extranet-icon"
                                alt="Your quote summary"
                            >

                            <p class="d-inline-block text-info">
                                Your quote summary
                            </p>
                        </a>
                    </div>
                    <div class="col-md-6 col-sm-12 p-2 text-left float-left">
                        <a
                            id="cover-letter-anchor"
                            target="_blank"
                            href="/static/pdf/offline_cover_letter.docx"
                        >
                            <img
                                src="@/assets/images/terms-conditions.svg"
                                class="d-inline-block extranet-icon"
                                alt="Cover Letter"
                            >

                            <p class="d-inline-block text-info">
                                Cover Letter
                            </p>
                        </a>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-lg-12 float-left m-2 bg-pdf mx-auto package-download-bottom">
            <h6 class="text-center blue chalet pt-3 font-weight-bold">
                Documents that have been generated for the top three results (Print from Extranet):
            </h6>
        </div>
        <div class="col-lg-12 float-left m-2 bg-pdf mx-auto package-download">
            <div class="row">
                <div class="col-12 p-2 text-left float-left">
                    <img
                        src="@/assets/images/buying-guide.svg"
                        class="extranet-icon"
                        alt="Key Facts"
                    >
                    <p class="d-inline-block text-info">
                        Key Facts
                    </p>
                </div>
                <div class="col-12 p-2 text-left float-left">
                    <img
                        src="@/assets/images/application.svg"
                        class="extranet-icon"
                        alt="Application PDF"
                    >
                    <p class="d-inline-block text-info">
                        Application PDF
                    </p>
                </div>
                <div class="col-12 p-2 text-left float-left">
                    <img
                        src="@/assets/images/medical.svg"
                        alt="Medical PDF"
                        class="extranet-icon"
                    >
                    <p class="d-inline-block text-info">
                        Medical PDF
                    </p>
                </div>
            </div>
        </div>
    </div>
    <div
        class="row apply-button-container"
    >
        <div
            class="col-12"
        >
            <div
                v-if="showExtranetHandoffContent"
            >
                <button
                    id="closeExtranetContentModal"
                    class="btn btn-warning btn-apply mt-2 mb-md-1 ml-md-5 mr-md-5"
                    @click.prevent="goToResults"
                >
                    Ok
                </button>
            </div>
            <div v-else>
                <button
                    v-if="isExtranetHandOff && state.appointmentChoice !== null"
                    id="generateQuotePackButton"
                    tabindex="0"
                    class="btn btn-warning btn-apply mt-2 mb-md-1 ml-md-5 mr-md-5"
                    @click="generateTopThreePacks"
                >
                    Generate Quote Pack
                </button>
                <div>
                    <button
                        :id="progressBlocked ? 'saveAndCloseButton' : `pw_apply_now_${getCompanyIdBySlug(applicationStore.companySlug)}`"
                        class="btn-apply btn mt-2 mb-md-1 ml-md-5 mr-md-5 btn-forward"
                        :disabled="progressBlocked"
                        @click.prevent="handleApplyClick"
                    >
                        Continue
                    </button>
                </div>
            </div>
        </div>
    </div>
    <Modal
        id="pensionwise-quote-summary-modal"
        open-focus="quote-summary-anchor"
        close-focus="quote-summary-anchor"
        header="Your quote summary"
        :show="state.showQuoteSummaryModal"
        @close="state.showQuoteSummaryModal = false"
    >
        <template #body>
            <QuoteSummary :quote="quote" />
        </template>
    </Modal>
</template>
<style scoped>
.small-text{
    font-size: 0.85rem;
}
.btn-danger{
    color: #fff;
    font-family: 'Poppins', sans-serif;
    background-color: #E5004A;
    border-radius: 3rem;
    line-height: 1.2rem;
    width: auto;
    font-size: 1rem;
    font-weight: 400;
    padding: 20px;
    border-color: #e05e52;
    width: 100%;
}
.previous-answer-heading {
    font-size: 16px;
}
.previous-answer-text {
    font-size: 14px;
}
.card-body{
    padding: 10px;
    border-bottom: 1px solid #000;
    border-left: 1px solid #000;
    border-right: 1px solid #000;
}
.extranet-icon {
    max-width: 32px;
    margin-right: 8px;
}
.alert-warning-box{
    position: relative;
    color: #2d2200;
    background-color: #fff3cd;
    border-color: #ffeeba;
    border-radius: 12px;
}
</style>
